import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, Query, QueryDocumentSnapshot, QuerySnapshot, startAfter, Timestamp, where, type DocumentData } from 'firebase/firestore';
import { db } from '../../../firebase';
// useAndreiChat removed 2026-05.
import { useEmailPanel, PanelEmailData } from '../mail/contexts/EmailPanelContext';
import JobEditModal from '../job-summary/JobEditModal';
import JobWorkspaceModal from '../job-summary/JobWorkspaceModal';
import type { FeedEmailData } from '../mail/components/EmailFeedContent';

type EntityRef = { type: 'email' | 'job' | 'task' | 'chat'; id: string; label?: string };

type ActivityEvent = {
  id: string;
  customerId: string;
  eventType: string;
  primaryEntity: EntityRef;
  relatedEntity?: EntityRef;
  title?: string;
  snippet?: string;
  actor?: string;
  direction?: 'in' | 'out';
  linkUrl?: string;
  timestamp?: Timestamp | Date | { seconds: number; nanoseconds: number } | null;
};

const INITIAL_LOAD = 20;
const LOAD_MORE_COUNT = 15;

const formatRelative = (ts?: ActivityEvent['timestamp']): string => {
  try {
    let date: Date | null = null;
    if (!ts) return '';
    if (ts instanceof Timestamp) date = ts.toDate();
    else if (ts instanceof Date) date = ts;
    else if (typeof ts === 'object' && ts && 'seconds' in ts) date = new Date(ts.seconds * 1000);
    if (!date) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return '';
  }
};

const getEventLabel = (eventType: string): string => {
  switch (eventType) {
    case 'email.received': return 'Email In';
    case 'email.sent': return 'Email Out';
    case 'chat.activity': return 'Chat';
    case 'job.created': return 'Job Created';
    case 'job.status': return 'Job Status';
    case 'task.created': return 'Task Created';
    case 'task.updated': return 'Task Updated';
    default: return eventType;
  }
};

const getEventBadgeClass = (eventType: string): string => {
  if (eventType.startsWith('email.')) return 'badge-light-primary';
  if (eventType.startsWith('job.')) return 'badge-light-success';
  if (eventType.startsWith('task.')) return 'badge-light-warning';
  if (eventType.startsWith('chat.')) return 'badge-light-info';
  return 'badge-light-secondary';
};

const truncateText = (text?: string | null, maxLen = 120): string => {
  const t = (text ?? '').toString().trim();
  if (!t) return '';
  return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
};

const startOfDay = (yyyyMmDd: string): Date => {
  const d = new Date(yyyyMmDd);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (yyyyMmDd: string): Date => {
  const d = new Date(yyyyMmDd);
  d.setHours(23, 59, 59, 999);
  return d;
};

const RecentClientActivity: React.FC = () => {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [customerNameById, setCustomerNameById] = useState<Record<string, string>>({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateStart, setDateStart] = useState<string>('');
  const [dateEnd, setDateEnd] = useState<string>('');
  // Chat-entity rows no-op since 2026-05.
  const { openEmailPanel, loadEmailsByIds, preLoadedEmails } = useEmailPanel();
  const lastDocRef = useRef<QueryDocumentSnapshot | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Modal state for jobs/tasks
  const [selectedJob, setSelectedJob] = useState<any | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showJobEditModal, setShowJobEditModal] = useState(false);
  const [initialTaskId, setInitialTaskId] = useState<string | undefined>(undefined);

  const rangeActive = !!(dateStart || dateEnd);

  const buildRangeConstraints = useCallback(() => {
    const constraints: any[] = [];
    if (dateStart) {
      constraints.push(where('timestamp', '>=', Timestamp.fromDate(startOfDay(dateStart))));
    }
    if (dateEnd) {
      constraints.push(where('timestamp', '<=', Timestamp.fromDate(endOfDay(dateEnd))));
    }
    return constraints;
  }, [dateStart, dateEnd]);

  // Real-time listener for initial batch (keeps newest events live)
  useEffect(() => {
    if (rangeActive) return;
    setIsLoading(true);
    setEvents([]);
    lastDocRef.current = null;
    setHasMore(true);
    const q = query(
      collection(db, 'customer_activity'),
      ...buildRangeConstraints(),
      orderBy('timestamp', 'desc'),
      limit(INITIAL_LOAD)
    );
    const unsub = onSnapshot(q, (snap) => {
      const rows: ActivityEvent[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      setEvents(rows);
      lastDocRef.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.docs.length === INITIAL_LOAD);
      setIsLoading(false);
    }, () => setIsLoading(false));
    return () => unsub();
  }, [buildRangeConstraints, rangeActive]);

  // Date-range mode: bypass incremental limits by fetching ALL events in range.
  // We intentionally use getDocs (not onSnapshot) to avoid large realtime listeners.
  useEffect(() => {
    if (!rangeActive) return;
    let cancelled = false;

    const PAGE = 500;

    (async () => {
      setIsLoading(true);
      setIsLoadingMore(true);
      setEvents([]);
      lastDocRef.current = null;
      setHasMore(false);

      let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
      const constraints = buildRangeConstraints();

      while (!cancelled) {
        const qRef: Query<DocumentData> = cursor
          ? query(
              collection(db, 'customer_activity'),
              ...constraints,
              orderBy('timestamp', 'desc'),
              startAfter(cursor),
              limit(PAGE)
            )
          : query(
              collection(db, 'customer_activity'),
              ...constraints,
              orderBy('timestamp', 'desc'),
              limit(PAGE)
            );

        const snap: QuerySnapshot<DocumentData> = await getDocs(qRef);
        if (cancelled) return;

        const batch: ActivityEvent[] = snap.docs.map((d: QueryDocumentSnapshot<DocumentData>) => ({ id: d.id, ...(d.data() as any) }));
        if (batch.length > 0) {
          setEvents((prev) => {
            const existingIds = new Set(prev.map((e) => e.id));
            const unique = batch.filter((e) => !existingIds.has(e.id));
            return [...prev, ...unique];
          });
        }

        // After first page, render results while continuing to fetch in background.
        setIsLoading(false);

        cursor = snap.docs[snap.docs.length - 1] || null;
        if (!cursor || snap.docs.length < PAGE) break;
      }
    })()
      .catch((err) => console.error('[RecentClientActivity] range fetch error:', err))
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [rangeActive, buildRangeConstraints]);

  // Load more events (pagination)
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !lastDocRef.current) return;
    setIsLoadingMore(true);
    try {
      const q = query(
        collection(db, 'customer_activity'),
        ...buildRangeConstraints(),
        orderBy('timestamp', 'desc'),
        startAfter(lastDocRef.current),
        limit(LOAD_MORE_COUNT)
      );
      const snap = await getDocs(q);
      const newRows: ActivityEvent[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }));
      if (newRows.length > 0) {
        setEvents((prev) => {
          // Dedupe by id in case of overlap
          const existingIds = new Set(prev.map((e) => e.id));
          const unique = newRows.filter((e) => !existingIds.has(e.id));
          return [...prev, ...unique];
        });
        lastDocRef.current = snap.docs[snap.docs.length - 1];
      }
      setHasMore(snap.docs.length === LOAD_MORE_COUNT);
    } catch (err) {
      console.error('[RecentClientActivity] loadMore error:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, buildRangeConstraints]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (rangeActive) return;
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    // Trigger load when within 100px of bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMore();
    }
  }, [loadMore, isLoadingMore, hasMore, rangeActive]);

  // Fetch customer names for display (cached)
  useEffect(() => {
    const uniqueIds = Array.from(new Set(events.map((e) => e.customerId).filter(Boolean)));
    const missing = uniqueIds.filter((id) => !customerNameById[id]);
    if (missing.length === 0) return;

    (async () => {
      const updates: Record<string, string> = {};
      await Promise.all(missing.map(async (id) => {
        try {
          const ref = doc(db, 'customers', id);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            const data = snap.data() as { name?: string; company?: string };
            const display = data?.company
              ? `${data.company}${data.name ? ` (${data.name})` : ''}`
              : (data?.name || id);
            updates[id] = display;
          } else {
            updates[id] = id;
          }
        } catch {
          updates[id] = id;
        }
      }));
      if (Object.keys(updates).length > 0) {
        setCustomerNameById((prev) => ({ ...prev, ...updates }));
      }
    })();
  }, [events, customerNameById]);

  // Handle opening different entity types
  const handleOpen = async (e: ActivityEvent) => {
    try {
      // Chat-entity rows no-op (2026-05).
      if (e.primaryEntity?.type === 'chat') return;
      if (e.primaryEntity?.type === 'email' && e.primaryEntity.id) {
        // Match EmailFeed behavior: open with FeedEmailData so the panel has the same
        // metadata + background Graph hydration behavior as a click in EmailFeedContent.
        const emailId = String(e.primaryEntity.id);

        // Prefer already-loaded email objects (instant open)
        const existing = (preLoadedEmails as FeedEmailData[]).find((em: any) => {
          const id = String(em?.id || em?.emailId || '');
          return id === emailId;
        });
        if (existing) {
          openEmailPanel('viewEmail', existing as any as PanelEmailData);
          return;
        }

        // Otherwise load by ID through the panel context (same code path as feed search results)
        const loaded = await loadEmailsByIds([emailId]);
        const first = loaded?.[0];
        if (first) {
          openEmailPanel('viewEmail', first as any as PanelEmailData);
          return;
        }

        // Fallback (should be rare): direct Firestore read
        const emailRef = doc(db, 'emails', emailId);
        const snap = await getDoc(emailRef);
        if (snap.exists()) {
          openEmailPanel('viewEmail', ({ id: snap.id, ...(snap.data() as any) }) as any as PanelEmailData);
          return;
        }
      }
    } catch (err) {
      console.error('[RecentClientActivity] handleOpen error:', err);
    }
  };

  const handleOpenTaskOrJob = async (e: ActivityEvent) => {
    try {
      if (e.primaryEntity?.type === 'task' && e.primaryEntity.id) {
        const taskRef = doc(db, 'tasks', e.primaryEntity.id);
        const snap = await getDoc(taskRef);
        const data = snap.exists() ? (snap.data() as any) : null;
        const jobId = data?.jobId;
        if (jobId) {
          const jobSnap = await getDoc(doc(db, 'jobs', jobId));
          if (jobSnap.exists()) {
            const jd = jobSnap.data() as any;
            const job = {
              id: jobSnap.id,
              jobLabel: jd.jobLabel || 'Job',
              description: jd.description || '',
              customerId: Array.isArray(jd.customerId) ? jd.customerId : (jd.customerId ? [jd.customerId] : []),
              unbilledHours: jd.unbilledHours ?? 0,
              totalHours: jd.totalHours ?? 0,
              creationDate: jd.creationDate || '',
              dueDate: jd.dueDate || '',
              status: jd.status || 'Open',
              lastEdited: jd.lastEdited || new Date().toISOString(),
            };
            setSelectedJob(job);
            setInitialTaskId(e.primaryEntity.id);
            setShowTaskModal(true);
            return;
          }
        }
      }
      if (e.primaryEntity?.type === 'job' && e.primaryEntity.id) {
        const jobSnap = await getDoc(doc(db, 'jobs', e.primaryEntity.id));
        if (jobSnap.exists()) {
          const jd = jobSnap.data() as any;
          const job = {
            id: jobSnap.id,
            jobLabel: jd.jobLabel || 'Job',
            description: jd.description || '',
            customerId: Array.isArray(jd.customerId) ? jd.customerId : (jd.customerId ? [jd.customerId] : []),
            unbilledHours: jd.unbilledHours ?? 0,
            totalHours: jd.totalHours ?? 0,
            creationDate: jd.creationDate || '',
            dueDate: jd.dueDate || '',
            status: jd.status || 'Open',
            lastEdited: jd.lastEdited || new Date().toISOString(),
          };
          setSelectedJob(job);
          setShowJobEditModal(true);
          return;
        }
      }
    } catch (err) {
      console.error('[RecentClientActivity] handleOpenTaskOrJob error:', err);
    }
  };

  const handleClick = (e: ActivityEvent) => {
    if (e.primaryEntity?.type === 'task' || e.primaryEntity?.type === 'job') {
      handleOpenTaskOrJob(e);
    } else {
      handleOpen(e);
    }
  };

  // Truncate title for compact display
  const truncateTitle = (title?: string, maxLen = 40): string => {
    if (!title) return '';
    return title.length > maxLen ? `${title.slice(0, maxLen)}…` : title;
  };

  const filteredEvents = (() => {
    const needle = (searchTerm || '').trim().toLowerCase();
    if (!needle) return events;
    return events.filter((e) => {
      const customerName = (customerNameById[e.customerId] || e.customerId || '').toLowerCase();
      const hay = `${e.title || ''} ${e.snippet || ''} ${e.eventType || ''} ${getEventLabel(e.eventType || '')} ${customerName}`.toLowerCase();
      return hay.includes(needle);
    });
  })();

  const hasAnyFilters = !!(searchTerm || dateStart || dateEnd);

  if (isLoading) {
    return (
      <>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Recent Client Activity</span>
          </h3>
        </div>
        <div className='card-body pt-5'>
          <div className='d-flex align-items-center mb-9 bg-light-primary rounded p-5'>
            <span className='spinner-border spinner-border-sm me-3' role='status'></span>
            <span>Loading activity...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className='card-header border-0 pt-5'>
        <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 w-100'>
          <div className='d-flex flex-column' style={{ minWidth: 220, maxWidth: '100%' }}>
            <span className='fw-bold fs-3'>Recent Client Activity</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              {hasAnyFilters
                ? `Showing ${filteredEvents.length} of ${events.length} loaded${rangeActive && isLoadingMore ? ' (loading more...)' : ''}`
                : `Latest ${events.length} events across all customers`}
            </span>
          </div>

          <div className='d-flex align-items-center flex-wrap flex-md-nowrap gap-2 ms-md-auto w-100 w-md-auto'>
            <input
              className='form-control form-control-sm'
              style={{ width: 220, minWidth: 180 }}
              placeholder='Search...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <input
              type='date'
              className='form-control form-control-sm w-auto'
              style={{ width: 155, minWidth: 155 }}
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              title='Start date'
            />
            <input
              type='date'
              className='form-control form-control-sm w-auto'
              style={{ width: 155, minWidth: 155 }}
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              title='End date'
            />
            {hasAnyFilters && (
              <button
                className='btn btn-sm btn-light'
                onClick={() => { setSearchTerm(''); setDateStart(''); setDateEnd(''); }}
              >
                Clear
              </button>
            )}
            <Link to='/customer-activity' className='btn btn-sm btn-light-primary'>
              View All
            </Link>
          </div>
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className='card-body pt-5'
        style={{ maxHeight: '1250px', overflowY: 'auto' }}
        onScroll={handleScroll}
      >
        {filteredEvents.length === 0 ? (
          <div className='text-center text-muted py-10'>
            No matching activity
          </div>
        ) : (
          <div className='timeline-label'>
            {filteredEvents.map((event) => (
              <div
                className='timeline-item'
                key={event.id}
                onClick={() => handleClick(event)}
                style={{ cursor: 'pointer' }}
              >
                <div className='timeline-label fw-bold text-gray-600 fs-7' style={{ minWidth: '55px' }}>
                  {formatRelative(event.timestamp)}
                </div>

                <div className='timeline-badge'>
                  <i
                    className='fa fa-genderless fs-1'
                    style={{
                      color: event.eventType.startsWith('email.')
                        ? '#3699FF'
                        : event.eventType.startsWith('job.')
                        ? '#1BC5BD'
                        : event.eventType.startsWith('task.')
                        ? '#FFA800'
                        : event.eventType.startsWith('chat.')
                        ? '#8950FC'
                        : '#B5B5C3'
                    }}
                  ></i>
                </div>

                <div className='fw-normal timeline-content ps-3 w-100' style={{ minWidth: 0 }}>
                  <div className='card card-bordered border-gray-200 shadow-sm w-100' style={{ maxWidth: '100%' }}>
                    <div className='card-body py-3 px-4'>
                      <div className='d-flex align-items-start justify-content-between gap-3'>
                        <div className='d-flex align-items-center flex-wrap gap-2' style={{ minWidth: 0, flex: 1 }}>
                          <span className={`badge ${getEventBadgeClass(event.eventType)} fs-8`}>
                            {getEventLabel(event.eventType)}
                          </span>
                          <span
                            className='text-gray-900 fw-semibold fs-7 text-truncate'
                            style={{ minWidth: 0 }}
                            title={event.title || ''}
                          >
                            {truncateTitle(event.title, 90)}
                          </span>
                        </div>

                        {/* Customer-focused right side (no IDs) */}
                        <div className='text-end' style={{ maxWidth: '45%', minWidth: 0 }}>
                          <div
                            className='fw-bold text-gray-900 fs-7 text-truncate'
                            style={{ minWidth: 0 }}
                            title={customerNameById[event.customerId] || event.customerId}
                          >
                            {customerNameById[event.customerId] || event.customerId}
                          </div>
                        </div>
                      </div>

                      {!!truncateText(event.snippet, 140) && (
                        <div className='text-muted fs-8 mt-1 text-truncate' title={truncateText(event.snippet, 500)}>
                          {truncateText(event.snippet, 140)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {/* Loading more indicator */}
            {isLoadingMore && (
              <div className='d-flex justify-content-center py-3'>
                <span className='spinner-border spinner-border-sm text-primary' role='status'></span>
              </div>
            )}
            {/* End of list indicator */}
            {!hasMore && events.length > INITIAL_LOAD && (
              <div className='text-center text-muted fs-8 py-3'>
                No more activity to load
              </div>
            )}
          </div>
        )}
      </div>

      {/* In-place modals for job/task */}
      {selectedJob && (
        <>
          <JobWorkspaceModal
            job={selectedJob}
            show={showTaskModal}
            onHide={() => { setShowTaskModal(false); setSelectedJob(null); setInitialTaskId(undefined); }}
          />
          <JobEditModal
            show={showJobEditModal}
            job={selectedJob}
            onHide={() => { setShowJobEditModal(false); setSelectedJob(null); }}
          />
        </>
      )}
    </>
  );
};

export default RecentClientActivity;
